import { List, Map } from 'immutable';
import { TypedMap } from 'flow/types/TypedMap';
import { Email } from 'flow/types/UserCredential';
import { isEmptyNullOrUndefined } from 'flow/utils';

const FAILURE_REASON_OCCUPIED = 'occupied_email';

export const getEmailAddress = (emailCredentials: List<TypedMap<Email>>) => {
    if (!emailCredentials) return null;

    const verifyingEmail = emailCredentials.find(
        (item) =>
            item.get('state') === 'verifying' ||
            item.get('state') === 'onboarding_verifying',
    );
    const validatedEmail = emailCredentials.find(
        (item) => item.get('state') === 'validated',
    );
    const legacyEmail = emailCredentials.find(
        (item) => item.get('state') === 'legacy',
    );

    return verifyingEmail || validatedEmail || legacyEmail;
};

export const canEditEmail = (emailCredentials: List<TypedMap<Email>>) => {
    const email = getEmailAddress(emailCredentials);
    return email?.get('editable', false);
};

const getValidatedEmailCredential = (
    emailCredentials: List<TypedMap<Email>>,
) => {
    if (!emailCredentials) {
        return null;
    }

    return emailCredentials
        .find(
            (credential) =>
                credential.get('state') === 'validated' ||
                credential.get('state') === 'legacy',
            null,
            Map(),
        )
        .get('credential', null);
};

export const getVerifyingEmailCredential = (
    emailCredentials: List<TypedMap<Email>>,
) => {
    if (!emailCredentials) {
        return null;
    }

    return emailCredentials
        .find(
            (credential) =>
                credential.get('state') === 'verifying' ||
                credential.get('state') === 'onboarding_verifying',
            null,
            Map(),
        )
        .get('credential', null);
};

export const getErrorTranslation = (
    emailInput: string,
    emailCredentials: List<TypedMap<Email>>,
    successCredential: boolean,
    updateFailureReason: string,
    errorCredential: string,
) => {
    if (getValidatedEmailCredential(emailCredentials) === emailInput)
        return 'settings.user.credential.own_validated_credential';

    if (getVerifyingEmailCredential(emailCredentials) === emailInput)
        return 'settings.user.credential.own_verifying_credential';

    return getErrorText(
        emailInput,
        successCredential,
        updateFailureReason,
        errorCredential,
    );
};

const getErrorText = (
    emailInput: string,
    successCredential: boolean,
    updateFailureReason: string,
    errorCredential: string,
) => {
    if (emailInput === '' || errorCredential !== emailInput) {
        return '';
    }
    if (updateFailureReason === FAILURE_REASON_OCCUPIED) {
        return 'settings.user.credential.occupied_credential_error';
    }
    if (!isEmptyNullOrUndefined(successCredential))
        return 'settings.user.credential.credential_error';

    return '';
};

export const getValidUserEmailOrNumber = (usernames: List<string>) => {
    const email = usernames && usernames.filter((o) => o.includes('@')).first();
    const number =
        usernames && usernames.filter((o) => /^\d+$/.test(o)).first();
    return email || number || null;
};
